import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact, map, reduce } from 'lodash';
import { EMPTY as empty, Observable, Subject, SubscriptionLike as ISubscription } from 'rxjs';
import { map as rxMap, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { IListViewData } from 'src/ng2/shared/typings/interfaces/list-view.interface';
import { LIST_TYPE } from '../../../../shared/constants/list-view/list-view.constant';
import { ListSummaryGroupings } from '../../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import {
  IMilestoneProjection,
  IStudentWithMilestones,
} from '../../../../shared/typings/interfaces/milestones.interface';
import { TValidSchoolTypes } from '../../../../shared/typings/interfaces/school.interface';
import { IUser } from '../../../../shared/typings/interfaces/user.interface';
import { ToggleBatchActions, UpdateSelectedStudentIds } from '../../../../store';
import { ListSearchComponent } from '../../list-search/list-search.component';
import { ISortData } from '../../my-students-list/my-students-mad-lib/my-students-mad-lib.interface';
import { ImUser } from './../../../../shared/services/im-models/im-user';
import { MadLibGroupings } from './../../../../shared/services/mad-lib-services/mad-lib-groupings';
import { MadLibHelpers } from './../../../../shared/services/mad-lib-services/mad-lib-helpers';
import { MadLibSorting } from './../../../../shared/services/mad-lib-services/mad-lib-sorting';
import { IPostsecondaryDimensionCol } from './postsecondary-mad-lib.constant';
/* istanbul ignore next */
@Component({
  selector: 'postsecondary-mad-lib',
  template: require('./postsecondary-mad-lib.component.html'),
})
export class PostsecondaryMadLib implements OnInit, OnDestroy {
  filteredPostsecondaryMadLib;
  sortState = { sortedColumn: null, sortOrder: null, dataType: null };
  destroy$: Subject<boolean> = new Subject<boolean>();
  listType: string = null;
  schoolType: TValidSchoolTypes;
  schoolId: string;
  madLibModels; // object each key being a dimension or grouping
  filteredStudents$: Observable<any[]>;
  columns; // object where each key is a number indicating the column order
  groupings$: Observable<any>;
  sortedGroupings$;
  selectedDimension1;
  selectedDimension2;
  batchEditMode: boolean;
  searchQuery: string;
  prevSearch: string;
  madlibStateSubscription: ISubscription;
  setPanelShadowStyle: boolean = false;

  @Input() currentSchool;
  @Input() currentUser: IUser;
  @Input() postsecStudents$: Observable<any>;
  @Input() madlibState$: Observable<any>;
  @Input() batchActionsMode$: Observable<any>;
  @ViewChild(ListSearchComponent) searchComponent: ListSearchComponent;

  constructor(
    private madLibGroupings: MadLibGroupings,
    private madLibHelpers: MadLibHelpers,
    private madLibSorting: MadLibSorting,
    private store: Store<any>,
    private listSummaryGroupings: ListSummaryGroupings,
    @Inject('$location') public $location: any,
    private imUser: ImUser,
  ) {
    this.selectedDimension1 = '';
    this.selectedDimension2 = '';
  }

  ngOnInit(): void {
    this.schoolType = this.currentSchool.schoolType;

    this.postsecStudents$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
      )
      .subscribe(() => this.updateGroupings());

    this.initializeInitialLoad();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  search(searchTerm) {
    const formattedSearchTerm = this.madLibHelpers.formatListSearchTerm(searchTerm);
    this.searchQuery = formattedSearchTerm;

    let searchStudents$ = this.madLibHelpers.getFilteredStudents(this.postsecStudents$, this.madLibModels.Dimension1);
    searchStudents$ = this.madLibHelpers.applySearchFilter(searchStudents$, formattedSearchTerm);
    this.groupings$ = this.getStudentGrouping(searchStudents$, this.madLibModels, this.columns);

    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );
    this.clearBatchEditsInProgress();
    this.madLibHelpers.updateUrl(this.madLibModels, {
      sortKey,
      dataType,
      orderBy,
      search: this.searchQuery,
      list: LIST_TYPE.POSTSECONDARY,
    });
  }

  clearSearch(): void {
    this.searchComponent.clearSearch();
  }

  filterStudents(newSort?: ISortData, newDimension?, newDimensionNum?: any): void {
    const isDimensionUpdate: boolean = !!newDimension;

    if (isDimensionUpdate) this.updateListDataOnDimensionChange(newDimension, newDimensionNum);

    this.clearBatchEditsInProgress();
    // always run this logic despite mode - sortUpdate, dimensionUpdate or initialLoad
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(newSort, newDimension, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
  }

  // wrapper for run mode side-effects
  initializeInitialLoad(): void {
    this.madlibStateSubscription = this.madlibState$.pipe(take(1)).subscribe((madlibState: any) => {
      this.filteredPostsecondaryMadLib = madlibState.filteredMadLib;
      this.madLibModels = madlibState.madLibModel;
    });

    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.postsecStudents$,
      this.madLibModels.Dimension1,
    );
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns);

    // always run this logic despite mode - sortUpdate, dimensionUpdate or initialLoad
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(null, null, validColumns);

    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      true,
      null,
      this.madLibModels,
    );

    this.sortState = this.madLibSorting.updateSortState(sortKey, orderBy, dataType);
    this.madLibHelpers.updateUrl(this.madLibModels, { sortKey, dataType, orderBy, search: this.searchQuery });
    const urlValues = this.$location.search();
    this.prevSearch = this.madLibHelpers.getSearchTerm(urlValues, LIST_TYPE.POSTSECONDARY);
  }

  updateGroupings(): void {
    this.batchActionsMode$
      .pipe(
        switchMap(mode => {
          return mode ? empty : this.madlibState$;
        }),
        tap((madlibState: any) => {
          if (madlibState) {
            this.filteredPostsecondaryMadLib = madlibState.filteredMadLib;
            this.madLibModels = this.madLibModels;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.postsecStudents$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns);
    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(true, true, validColumns);
    this.sortedGroupings$ = this.madLibHelpers.getSortedStudentGrouping(
      this.groupings$,
      sortKey,
      dataType,
      orderBy,
      this.sortState,
      false,
      null,
      this.madLibModels,
    );
  }

  // wrapper for run mode side-effects
  updateListDataOnDimensionChange(newDimension, newDimensionNum: any): void {
    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };
    this.madLibModels = this.madLibHelpers.updateMadLibModel(this.madLibModels, newDimensionNum, newDimension);
    this.columns = this.madLibHelpers.getListColumns(this.madLibModels);
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.postsecStudents$,
      this.madLibModels.Dimension1,
    );
    this.filteredStudents$ = this.madLibHelpers.applySearchFilter(this.filteredStudents$, this.searchQuery);
    this.groupings$ = this.getStudentGrouping(this.filteredStudents$, this.madLibModels, this.columns);
  }

  filterStudentsByMilestoneType(postsecStudents$, dim2Key: string): Observable<any> {
    return postsecStudents$.pipe(
      rxMap((students: any[]) => {
        // a milestone can appear in both lists.
        if (dim2Key === 'COLLEGE_MILESTONES') {
          return reduce(
            students,
            (acc, student: IStudentWithMilestones) => {
              student.milestones = compact(
                map(student.milestones, (milestone: IMilestoneProjection) => {
                  if (milestone.isCollegeTrack) return milestone;
                }),
              );
              acc.push(student);
              return acc;
            },
            [],
          );
        }

        if (dim2Key === 'CAREER_MILESTONES') {
          return reduce(
            students,
            (acc, student: IStudentWithMilestones) => {
              student.milestones = compact(
                map(student.milestones, (milestone: IMilestoneProjection) => {
                  if (milestone.isCareerTrack) return milestone;
                }),
              );
              acc.push(student);
              return acc;
            },
            [],
          );
        }

        if (dim2Key === 'APPLICATION_STATUS') return students;
      }),
    );
  }

  getValidColumns(columns: any): string[] {
    return columns.map(({ key }) => key);
  }

  getStudentGrouping(
    filteredStudents$,
    madLibModels,
    columns: IPostsecondaryDimensionCol,
  ): Observable<Partial<IListViewData>> {
    const {
      Dimension2: { key },
    } = madLibModels;
    let groupings$: Observable<Partial<IListViewData>>;
    switch (key) {
      case 'COLLEGE_MILESTONES':
        const studentsCollegeMilestones$ = this.filterStudentsByMilestoneType(filteredStudents$, key);
        groupings$ = this.madLibGroupings.getPostsecondaryMilestonesStatusGrouping(
          studentsCollegeMilestones$,
          columns,
          LIST_TYPE.POSTSECONDARY,
        );
        break;
      case 'CAREER_MILESTONES':
        const studentsCareerMilestones$ = this.filterStudentsByMilestoneType(filteredStudents$, key);
        groupings$ = this.madLibGroupings.getPostsecondaryMilestonesStatusGrouping(
          studentsCareerMilestones$,
          columns,
          LIST_TYPE.POSTSECONDARY,
        );
        break;
      case 'APPLICATION_STATUS':
        groupings$ = this.madLibGroupings.getPostsecondaryApplicationStatusGrouping(
          filteredStudents$,
          columns,
          LIST_TYPE.POSTSECONDARY_ALT,
        );
        break;
      default:
        groupings$ = null;
        break;
    }
    this.listSummaryGroupings.setGroupings(madLibModels, groupings$);
    return groupings$;
  }

  // triggers change detection on mousemove. rethink
  isOptionActive(lib, option: string) {
    if (lib.madLib === 'Dimension1') return this.selectedDimension1.key === option;
    return this.selectedDimension2.key === option;
  }

  clearBatchEditsInProgress(): void {
    this.batchEditMode = false;
    this.store.dispatch(new ToggleBatchActions(this.batchEditMode));
    this.store.dispatch(new UpdateSelectedStudentIds([]));
  }
}
